import MaskedView from "@react-native-masked-view/masked-view";
import { LinearGradient } from "expo-linear-gradient";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import type { DimensionValue } from "react-native";
import {
  Animated,
  Easing,
  Image,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Svg, { Defs, FeDropShadow, Filter, Path } from "react-native-svg";

import {
  FEATURED_ACCESS_CARD_GEOMETRY,
  FEATURED_ACCESS_CARD_CONTENT_PADDING,
  FEATURED_ACCESS_CARD_COPY_GAP,
  FEATURED_ACCESS_CARD_HEIGHT,
  FEATURED_ACCESS_CARD_RADIUS,
  FEATURED_ACCESS_CARD_SECONDARY_TEXT_COLOR,
  FEATURED_ACCESS_CARD_SEGMENT_SKEW_DEGREES,
  FEATURED_ACCESS_CARD_SHIMMER_WIDTH_PERCENT,
  FEATURED_ACCESS_CARD_TICKET_CUTOUT_RADIUS,
  FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT,
  createFeaturedAccessCardActionColors,
  createFeaturedAccessCardDateTileGradient,
  createFeaturedAccessCardGradients,
  resolveFeaturedAccessCardShimmer,
} from "./recipe";
import type {
  FeaturedAccessCardAction,
  FeaturedAccessCardBackground,
  FeaturedAccessCardCopy,
  FeaturedAccessCardLeadingVisual,
} from "./schema";
import type { FeaturedAccessCardProps } from "./types";
import { LockBadge } from "../lock-badge/native";
import { ExclusiveRibbon } from "../exclusive-ribbon/native";

const percent = (value: number) => `${value}%` as DimensionValue;
const PERFORATION_DASH_COUNT = 11;
const BACKGROUND_SOURCES: Record<FeaturedAccessCardBackground, number> = {
  concert: require("../../assets/featured-access-card/concert.webp") as number,
  song: require("../../assets/featured-access-card/song.webp") as number,
};

function createTicketMaskPath(width: number) {
  const height = FEATURED_ACCESS_CARD_HEIGHT;
  const radius = FEATURED_ACCESS_CARD_RADIUS;
  const cutoutRadius = FEATURED_ACCESS_CARD_TICKET_CUTOUT_RADIUS;
  const cutoutX = width * (FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT / 100);
  const circle = (centerY: number) =>
    `M ${cutoutX - cutoutRadius} ${centerY} ` +
    `A ${cutoutRadius} ${cutoutRadius} 0 1 0 ${cutoutX + cutoutRadius} ${centerY} ` +
    `A ${cutoutRadius} ${cutoutRadius} 0 1 0 ${cutoutX - cutoutRadius} ${centerY} Z`;

  return (
    `M ${radius} 0 H ${width - radius} ` +
    `A ${radius} ${radius} 0 0 1 ${width} ${radius} ` +
    `V ${height - radius} A ${radius} ${radius} 0 0 1 ${width - radius} ${height} ` +
    `H ${radius} A ${radius} ${radius} 0 0 1 0 ${height - radius} ` +
    `V ${radius} A ${radius} ${radius} 0 0 1 ${radius} 0 Z ` +
    `${circle(0)} ${circle(height)}`
  );
}

function TicketMask({ width }: { width: number }) {
  return (
    <Svg height={FEATURED_ACCESS_CARD_HEIGHT} width={width}>
      <Path d={createTicketMaskPath(width)} fill="#FFFFFF" fillRule="evenodd" />
    </Svg>
  );
}

/**
 * react-native-web has no MaskedView implementation (the card rendered as a
 * blank white box on the web app, LIS-548), so on web the same ticket path is
 * applied as a CSS mask on a plain DOM element instead.
 */
function WebTicketMask({
  children,
  width,
}: {
  children: ReactNode;
  width: number;
}) {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" ` +
    `height="${FEATURED_ACCESS_CARD_HEIGHT}">` +
    `<path d="${createTicketMaskPath(width)}" fill="#fff" fill-rule="evenodd"/>` +
    `</svg>`;
  const maskImage = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        WebkitMaskImage: maskImage,
        maskImage,
        WebkitMaskSize: "100% 100%",
        maskSize: "100% 100%",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
      }}
    >
      {children}
    </div>
  );
}

function TicketShadow({
  width,
  fill,
}: {
  width: number;
  fill: string | undefined;
}) {
  return (
    <Svg
      height={FEATURED_ACCESS_CARD_HEIGHT}
      pointerEvents="none"
      style={styles.ticketShadow}
      width={width}
    >
      <Defs>
        <Filter
          id="featured-access-card-shadow"
          height="200%"
          width="200%"
          x="-50%"
          y="-50%"
        >
          <FeDropShadow
            dx={0}
            dy={10}
            floodColor="#211D18"
            floodOpacity={0.2}
            stdDeviation={9}
          />
        </Filter>
      </Defs>
      <Path
        d={createTicketMaskPath(width)}
        fill={fill}
        fillRule="evenodd"
        filter="url(#featured-access-card-shadow)"
      />
    </Svg>
  );
}

function LeadingVisualFrame({
  accessibilityLabel,
  background,
  children,
}: {
  accessibilityLabel?: string;
  background: ReactNode;
  children: ReactNode;
}) {
  return (
    <View style={styles.leadingVisualFrame}>
      <View
        accessibilityLabel={accessibilityLabel}
        style={styles.leadingVisualClip}
      >
        {background}
        {children}
      </View>
    </View>
  );
}

function EventDateVisual({
  visual,
  accentColor,
}: {
  visual: Extract<FeaturedAccessCardLeadingVisual, { type: "event-date" }>;
  accentColor: string;
}) {
  const dateTileGradient =
    createFeaturedAccessCardDateTileGradient(accentColor);

  return (
    <LeadingVisualFrame
      accessibilityLabel={[visual.month, visual.day, visual.year]
        .filter(Boolean)
        .join(" ")}
      background={
        <LinearGradient
          colors={dateTileGradient}
          end={{ x: 1, y: 1 }}
          start={{ x: 0, y: 0 }}
          style={StyleSheet.absoluteFill}
        />
      }
    >
      <View style={styles.dateStack}>
        <Text style={styles.dateMonth}>{visual.month}</Text>
        <Text style={styles.dateDay}>{visual.day}</Text>
        {visual.year ? (
          <Text style={styles.dateYear}>{visual.year}</Text>
        ) : null}
      </View>
    </LeadingVisualFrame>
  );
}

function ExclusiveArtworkVisual({
  visual,
  accentColor,
}: {
  visual: Extract<
    FeaturedAccessCardLeadingVisual,
    { type: "exclusive-artwork" }
  >;
  accentColor: string;
}) {
  const fallbackGradient =
    createFeaturedAccessCardDateTileGradient(accentColor);

  return (
    <LeadingVisualFrame
      background={
        <LinearGradient
          colors={fallbackGradient}
          end={{ x: 1, y: 1 }}
          start={{ x: 0, y: 0 }}
          style={StyleSheet.absoluteFill}
        />
      }
    >
      <Image
        accessible={false}
        resizeMode="cover"
        source={{ uri: visual.artworkUrl }}
        style={styles.artwork}
      />
      <ExclusiveRibbon containerSize={56} variant="compact" />
    </LeadingVisualFrame>
  );
}

function LeadingVisual({
  visual,
  accentColor,
}: {
  visual: FeaturedAccessCardLeadingVisual;
  accentColor: string;
}) {
  return visual.type === "event-date" ? (
    <EventDateVisual visual={visual} accentColor={accentColor} />
  ) : (
    <ExclusiveArtworkVisual visual={visual} accentColor={accentColor} />
  );
}

function Shimmer({
  cardWidth,
  durationMs,
  intervalMs,
}: {
  cardWidth: number;
  durationMs: number;
  intervalMs: number;
}) {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (cardWidth <= 0) return;

    progress.setValue(0);
    const animations: Animated.CompositeAnimation[] = [
      Animated.timing(progress, {
        toValue: 1,
        duration: durationMs,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }),
    ];
    const restMs = intervalMs - durationMs;
    if (restMs > 0) animations.push(Animated.delay(restMs));

    const loop = Animated.loop(Animated.sequence(animations));
    loop.start();

    return () => loop.stop();
  }, [cardWidth, durationMs, intervalMs, progress]);

  if (cardWidth <= 0) return null;

  const shimmerWidth =
    cardWidth * (FEATURED_ACCESS_CARD_SHIMMER_WIDTH_PERCENT / 100);
  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [-shimmerWidth, cardWidth],
  });
  const opacity = progress.interpolate({
    inputRange: [0, 0.12, 0.88, 1],
    outputRange: [0, 1, 1, 0],
  });

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.shimmer,
        { opacity, transform: [{ translateX }, { skewX: "-18deg" }] },
      ]}
    >
      <LinearGradient
        colors={[
          "rgba(255, 255, 255, 0)",
          "rgba(255, 255, 255, 0.13)",
          "rgba(255, 255, 255, 0.52)",
          "rgba(255, 255, 255, 0.13)",
          "rgba(255, 255, 255, 0)",
        ]}
        end={{ x: 1, y: 0.5 }}
        locations={[0, 0.3, 0.5, 0.7, 1]}
        start={{ x: 0, y: 0.5 }}
        style={StyleSheet.absoluteFill}
      />
    </Animated.View>
  );
}

function CardCopy({ copy }: { copy: FeaturedAccessCardCopy }) {
  return (
    <View style={styles.copy}>
      <Text numberOfLines={1} style={styles.copyTitle}>
        {copy.title}
      </Text>
      <Text numberOfLines={1} style={styles.copySubtitle}>
        {copy.subtitle}
      </Text>
    </View>
  );
}

function CardAction({
  accentColor,
  action,
}: {
  accentColor: string;
  action: FeaturedAccessCardAction;
}) {
  const colors = createFeaturedAccessCardActionColors(accentColor);

  return (
    <View style={styles.actionArea}>
      <View
        style={[
          styles.actionButton,
          action.locked ? styles.actionButtonLocked : undefined,
          action.value
            ? styles.actionButtonWithValue
            : styles.actionButtonLabelOnly,
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            styles.actionLabel,
            {
              color: colors.value,
              textShadowColor: action.locked ? colors.value : undefined,
            },
            action.locked ? styles.actionCopyLocked : undefined,
          ]}
        >
          {action.label}
        </Text>
        {action.value ? (
          <View style={styles.actionValues}>
            {action.previousValue ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.actionPreviousValue,
                  {
                    color: colors.previousValue,
                    textDecorationColor: colors.previousValue,
                  },
                ]}
              >
                {action.previousValue}
              </Text>
            ) : null}
            <Text
              numberOfLines={1}
              style={[styles.actionValue, { color: colors.value }]}
            >
              {action.value}
            </Text>
          </View>
        ) : null}
        {action.locked ? (
          <LockBadge size={22} style={styles.actionLockBadge} />
        ) : null}
      </View>
    </View>
  );
}

export function FeaturedAccessCard({
  background,
  backgroundImageUrl,
  accentColor,
  leadingVisual,
  copy,
  action,
  locked = false,
  shimmer,
  children,
}: FeaturedAccessCardProps) {
  const [cardWidth, setCardWidth] = useState(0);
  const gradients = createFeaturedAccessCardGradients(accentColor);
  const shimmerTiming = resolveFeaturedAccessCardShimmer(shimmer);
  const backgroundColor = gradients.at(-1)?.colors.at(-1);
  const handleLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    if (nativeEvent.layout.width !== cardWidth) {
      setCardWidth(nativeEvent.layout.width);
    }
  };

  const card = (
    <View style={[styles.root, { backgroundColor }]}>
      <Image
        accessible={false}
        resizeMode="cover"
        source={
          backgroundImageUrl
            ? { uri: backgroundImageUrl }
            : BACKGROUND_SOURCES[background]
        }
        style={styles.image}
      />
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        {FEATURED_ACCESS_CARD_GEOMETRY.segments.map((segment, index) => {
          const gradient = gradients[segment.paletteIndex];

          return (
            <LinearGradient
              key={index}
              colors={gradient.colors}
              end={{ x: 1, y: 0.5 }}
              locations={gradient.locations}
              start={{ x: 0, y: 0.5 }}
              style={[
                styles.segment,
                {
                  left: percent(segment.leftPercent),
                  width: percent(segment.widthPercent),
                },
              ]}
            />
          );
        })}
      </View>
      <Shimmer cardWidth={cardWidth} {...shimmerTiming} />
      <View pointerEvents="none" style={styles.perforation}>
        {Array.from({ length: PERFORATION_DASH_COUNT }, (_, index) => (
          <View key={index} style={styles.perforationDash} />
        ))}
      </View>
      {locked ? <LockBadge style={styles.lockBadge} /> : null}
      <View style={styles.content}>
        <View style={styles.leftContent}>
          {leadingVisual ? (
            <LeadingVisual visual={leadingVisual} accentColor={accentColor} />
          ) : null}
          {copy ? <CardCopy copy={copy} /> : null}
        </View>
        {action ? (
          <CardAction action={action} accentColor={accentColor} />
        ) : null}
        {children}
      </View>
    </View>
  );

  return (
    <View
      testID="featured-access-card"
      style={styles.shadow}
      onLayout={handleLayout}
    >
      {cardWidth > 0 ? (
        Platform.OS === "web" ? (
          // No TicketShadow on web: react-native-svg renders it unclipped and
          // unfiltered there, so its divider circles show as solid dots. A CSS
          // drop-shadow around the masked element follows the ticket
          // silhouette (notches included) instead.
          <div
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 18px rgba(33, 29, 24, 0.2))",
            }}
          >
            <WebTicketMask width={cardWidth}>{card}</WebTicketMask>
          </div>
        ) : (
          <>
            <TicketShadow fill={backgroundColor} width={cardWidth} />
            <MaskedView
              maskElement={<TicketMask width={cardWidth} />}
              style={styles.mask}
            >
              {card}
            </MaskedView>
          </>
        )
      ) : (
        card
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  shadow: {
    position: "relative",
    width: "100%",
    height: FEATURED_ACCESS_CARD_HEIGHT,
  },
  ticketShadow: {
    position: "absolute",
    inset: 0,
    overflow: "visible",
  },
  mask: {
    width: "100%",
    height: "100%",
  },
  root: {
    position: "relative",
    width: "100%",
    height: FEATURED_ACCESS_CARD_HEIGHT,
    overflow: "hidden",
    borderRadius: FEATURED_ACCESS_CARD_RADIUS,
  },
  image: {
    position: "absolute",
    inset: 0,
    width: percent(FEATURED_ACCESS_CARD_GEOMETRY.imageWidthPercent),
    height: "100%",
  },
  segment: {
    position: "absolute",
    top: "-20%",
    height: "140%",
    transform: [{ skewX: `${FEATURED_ACCESS_CARD_SEGMENT_SKEW_DEGREES}deg` }],
  },
  shimmer: {
    position: "absolute",
    zIndex: 2,
    top: "-35%",
    left: 0,
    width: percent(FEATURED_ACCESS_CARD_SHIMMER_WIDTH_PERCENT),
    height: "170%",
  },
  perforation: {
    position: "absolute",
    zIndex: 2,
    top: FEATURED_ACCESS_CARD_TICKET_CUTOUT_RADIUS,
    bottom: FEATURED_ACCESS_CARD_TICKET_CUTOUT_RADIUS,
    left: percent(FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT),
    width: 2,
    alignItems: "center",
    justifyContent: "space-between",
    transform: [{ translateX: -1 }],
  },
  perforationDash: {
    width: 2,
    height: 4,
    borderRadius: 1,
    backgroundColor: "rgba(255, 255, 255, 0.42)",
  },
  lockBadge: {
    position: "absolute",
    zIndex: 5,
    top: "50%",
    left: percent(FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT),
    transform: [{ translateX: -10 }, { translateY: -10 }],
  },
  leadingVisualFrame: {
    height: "100%",
    aspectRatio: 1,
    flexShrink: 0,
    borderRadius: 14,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.24,
    shadowRadius: 5,
    elevation: 4,
  },
  leadingVisualClip: {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.22)",
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  dateMonth: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 1.5,
    lineHeight: 12,
    textTransform: "uppercase",
  },
  dateStack: {
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  dateDay: {
    color: "#FFFFFF",
    fontSize: 25,
    fontWeight: "800",
    letterSpacing: -0.8,
    lineHeight: 26,
  },
  dateYear: {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.6,
    lineHeight: 10,
  },
  artwork: {
    width: "100%",
    height: "100%",
  },
  content: {
    position: "absolute",
    inset: 0,
    zIndex: 3,
    flexDirection: "row",
  },
  leftContent: {
    width: percent(FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT),
    height: "100%",
    flexDirection: "row",
    alignItems: "stretch",
    gap: FEATURED_ACCESS_CARD_CONTENT_PADDING,
    padding: FEATURED_ACCESS_CARD_CONTENT_PADDING,
  },
  copy: {
    minWidth: 0,
    flex: 1,
    justifyContent: "center",
    gap: FEATURED_ACCESS_CARD_COPY_GAP,
  },
  copyTitle: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "800",
    lineHeight: 19,
    textShadowColor: "rgba(0, 0, 0, 0.62)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  copySubtitle: {
    color: FEATURED_ACCESS_CARD_SECONDARY_TEXT_COLOR,
    fontSize: 13,
    fontWeight: "500",
    lineHeight: 15,
    textShadowColor: "rgba(0, 0, 0, 0.62)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  actionArea: {
    width: percent(100 - FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT),
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    padding: FEATURED_ACCESS_CARD_CONTENT_PADDING,
  },
  actionButton: {
    position: "relative",
    maxWidth: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    borderRadius: 9,
    backgroundColor: "#FFFFFF",
  },
  actionButtonLocked: {
    overflow: "hidden",
  },
  actionButtonLabelOnly: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  actionButtonWithValue: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  actionLabel: {
    fontSize: 11,
    fontWeight: "800",
    lineHeight: 12,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  actionCopyLocked: {
    opacity: 0.55,
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 4,
  },
  actionLockBadge: {
    position: "absolute",
    zIndex: 1,
    top: "50%",
    left: "50%",
    transform: [{ translateX: -11 }, { translateY: -11 }],
  },
  actionValues: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
  },
  actionPreviousValue: {
    fontSize: 13,
    fontWeight: "700",
    lineHeight: 14,
    textDecorationLine: "line-through",
  },
  actionValue: {
    fontSize: 13,
    fontWeight: "800",
    lineHeight: 14,
  },
});
